<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'
import Stats from 'stats.js'
import {
  AnimationPlugin,
  presets,
  type AnimationClipData,
  type AnimationEasing,
  type AnimationInterpolation,
  type AnimationLoopMode,
  type AnimationPlayerHandle,
  type AnimationService,
  type AnimationTrackData,
  type CameraStatePatch,
  type GaussianSplatPlugin,
  PointCloudPlugin,
} from '@sharefe/share3d-engine'
import { getDefaultPresetModelUrl, PRESET_MODELS } from '../examples/presetModels'
import { useExampleGraphicsCard } from '../composables/useExampleGraphicsCard'
import { resolvePointCloudInputPath } from '../utils/assetPath'
import { downloadBlob } from '../utils/downloadBlob'
import {
  createCameraAnimationKeyframeFile,
  parseCameraAnimationKeyframeFile,
  stringifyCameraAnimationKeyframeFile,
  type CameraAnimationKeyframeFile,
} from '../utils/cameraAnimationKeyframes'
import { toGraphicsRuntimeError } from '../utils/graphicsRuntimeError'
import {
  readPointCloudDemoUrlConfig,
  reloadPointCloudDemoConfig,
  type PointCloudDemoUrlPatch,
  type PointDrawMode,
} from '../utils/pointCloudDemoUrlConfig'
import {
  applySpaceMouseNavigationProfile,
  type SpaceMouseControllerLike,
} from '../utils/spaceMouseNavigation'
import { use3DGS } from '../hooks/use3DGS'
import {
  configureGsplatPlugins,
  getGsplatGraphicsConfig,
  readGsplatBackendControlState,
} from '../utils/gsplatBackendControl'
import SceneAssetLoaderCard from './SceneAssetLoaderCard.vue'
import GSplatBackendControlCard from './GSplatBackendControlCard.vue'
import GraphicsBackendControlCard from './GraphicsBackendControlCard.vue'
import SceneControlPanel from './SceneControlPanel.vue'
import SceneGroupCard from './SceneGroupCard.vue'
import ScenePointBudgetCard from './ScenePointBudgetCard.vue'

type CameraEasingMode = 'none' | AnimationEasing

interface CameraInterpolationOption {
  value: AnimationInterpolation
  label: string
  detail: string
}

interface CameraEasingOption {
  value: CameraEasingMode
  label: string
  detail: string
}

interface CameraLoopModeOption {
  value: AnimationLoopMode
  label: string
  detail: string
}

interface CameraEasingGroup {
  title: string
  options: CameraEasingOption[]
}

interface CameraKeyframe {
  id: number
  time: number
  position: [number, number, number]
  target: [number, number, number]
  fov: number
}

type CameraFramePose = Pick<CameraKeyframe, 'position' | 'target' | 'fov'>
type CameraVectorField = 'position' | 'target'
type CameraVectorAxis = 0 | 1 | 2

const KEYFRAME_TIME_STEP = 2
const MIN_KEYFRAME_TIME_GAP = 0.01
const CAMERA_FOV_MIN = 20
const CAMERA_FOV_MAX = 100
const CAMERA_VECTOR_AXES: { label: string; axis: CameraVectorAxis }[] = [
  { label: 'X', axis: 0 },
  { label: 'Y', axis: 1 },
  { label: 'Z', axis: 2 },
]

const DEFAULT_CAMERA_KEYFRAMES: Omit<CameraKeyframe, 'id'>[] = [
  {
    time: 0,
    position: [4, -8, 5],
    target: [0, 0, 1],
    fov: 65,
  },
  {
    time: 2,
    position: [-6, -6, 4.5],
    target: [0, 0, 1.2],
    fov: 58,
  },
  {
    time: 4,
    position: [-7, 3, 4],
    target: [0, 0, 1],
    fov: 62,
  },
  {
    time: 6,
    position: [3, 7, 5],
    target: [0, 0, 1.1],
    fov: 70,
  },
  {
    time: 8,
    position: [4, -8, 5],
    target: [0, 0, 1],
    fov: 65,
  },
]

const CAMERA_INTERPOLATION_OPTIONS: CameraInterpolationOption[] = [
  {
    value: 'linear',
    label: '线性插值',
    detail: 'linear',
  },
  {
    value: 'spline',
    label: '样条插值',
    detail: 'spline smoothness 1',
  },
  {
    value: 'step',
    label: '阶梯切换',
    detail: 'step',
  },
]

const CAMERA_LOOP_MODE_OPTIONS: CameraLoopModeOption[] = [
  {
    value: 'once',
    label: '播放一次',
    detail: '末帧停止',
  },
  {
    value: 'repeat',
    label: '循环播放',
    detail: '回到起点继续',
  },
  {
    value: 'pingpong',
    label: '往返播放',
    detail: '正放后倒放',
  },
]

const CAMERA_EASING_GROUPS: CameraEasingGroup[] = [
  {
    title: '基础',
    options: [
      {
        value: 'none',
        label: '无缓动',
        detail: '原始时间进度',
      },
      {
        value: 'linear',
        label: '匀速',
        detail: 'linear',
      },
    ],
  },
  {
    title: '二次 Quadratic',
    options: [
      {
        value: 'quadraticIn',
        label: '缓入',
        detail: 'quadraticIn',
      },
      {
        value: 'quadraticOut',
        label: '缓出',
        detail: 'quadraticOut',
      },
      {
        value: 'quadraticInOut',
        label: '缓入缓出',
        detail: 'quadraticInOut',
      },
    ],
  },
  {
    title: '三次 Cubic',
    options: [
      {
        value: 'cubicIn',
        label: '缓入',
        detail: 'cubicIn',
      },
      {
        value: 'cubicOut',
        label: '缓出',
        detail: 'cubicOut',
      },
      {
        value: 'cubicInOut',
        label: '缓入缓出',
        detail: 'cubicInOut',
      },
    ],
  },
  {
    title: '四次 Quartic',
    options: [
      {
        value: 'quarticIn',
        label: '缓入',
        detail: 'quarticIn',
      },
      {
        value: 'quarticOut',
        label: '缓出',
        detail: 'quarticOut',
      },
      {
        value: 'quarticInOut',
        label: '缓入缓出',
        detail: 'quarticInOut',
      },
    ],
  },
  {
    title: '五次 Quintic',
    options: [
      {
        value: 'quinticIn',
        label: '缓入',
        detail: 'quinticIn',
      },
      {
        value: 'quinticOut',
        label: '缓出',
        detail: 'quinticOut',
      },
      {
        value: 'quinticInOut',
        label: '缓入缓出',
        detail: 'quinticInOut',
      },
    ],
  },
  {
    title: '正弦 Sine',
    options: [
      {
        value: 'sineIn',
        label: '柔和入',
        detail: 'sineIn',
      },
      {
        value: 'sineOut',
        label: '柔和出',
        detail: 'sineOut',
      },
      {
        value: 'sineInOut',
        label: '柔和进出',
        detail: 'sineInOut',
      },
    ],
  },
  {
    title: '指数 Expo',
    options: [
      {
        value: 'expoIn',
        label: '急加速入',
        detail: 'expoIn',
      },
      {
        value: 'expoOut',
        label: '急减速出',
        detail: 'expoOut',
      },
      {
        value: 'expoInOut',
        label: '指数进出',
        detail: 'expoInOut',
      },
    ],
  },
  {
    title: '圆形 Circ',
    options: [
      {
        value: 'circIn',
        label: '圆弧入',
        detail: 'circIn',
      },
      {
        value: 'circOut',
        label: '圆弧出',
        detail: 'circOut',
      },
      {
        value: 'circInOut',
        label: '圆弧进出',
        detail: 'circInOut',
      },
    ],
  },
  {
    title: '弹性 Elastic',
    options: [
      {
        value: 'elasticIn',
        label: '弹性入',
        detail: 'elasticIn',
      },
      {
        value: 'elasticOut',
        label: '弹性出',
        detail: 'elasticOut',
      },
      {
        value: 'elasticInOut',
        label: '弹性进出',
        detail: 'elasticInOut',
      },
    ],
  },
  {
    title: '回弹 Back',
    options: [
      {
        value: 'backIn',
        label: '回拉入',
        detail: 'backIn',
      },
      {
        value: 'backOut',
        label: '回弹出',
        detail: 'backOut',
      },
      {
        value: 'backInOut',
        label: '回弹进出',
        detail: 'backInOut',
      },
    ],
  },
  {
    title: '弹跳 Bounce',
    options: [
      {
        value: 'bounceIn',
        label: '弹跳入',
        detail: 'bounceIn',
      },
      {
        value: 'bounceOut',
        label: '弹跳出',
        detail: 'bounceOut',
      },
      {
        value: 'bounceInOut',
        label: '弹跳进出',
        detail: 'bounceInOut',
      },
    ],
  },
]

const CAMERA_EASING_OPTIONS = CAMERA_EASING_GROUPS.flatMap((group) => group.options)

// 默认加载预设 model1
const DEFAULT_GS3D_MODEL = getDefaultPresetModelUrl()

const containerRef = useTemplateRef('containerRef')
const backendControlState = readGsplatBackendControlState()
const graphicsCard = useExampleGraphicsCard({
  presetKey: 'pointCloudAndGSplat',
  extras: ['AnimationPlugin'],
})
// 与 gs3d demo 共用预设闭包：点云与 3DGS 都随 preset 注册，import 卡片可加载两类资产。
const plugins = [
  ...configureGsplatPlugins(presets.pointCloudAndGSplat(), backendControlState),
  new AnimationPlugin(),
]

const {
  engine,
  isLoading,
  modelInput,
  stats,
  init,
  loadModel,
  removeAllModels,
  autoLoad,
} = use3DGS(containerRef, {
  plugins,
  defaultModelUrl: DEFAULT_GS3D_MODEL,
  autoLoadFromUrl: true,
  graphics: getGsplatGraphicsConfig(backendControlState),
  fov: 65,
  background: 'black',
  fitOnLoad: false,
})

type CameraAnimationAssetKind = 'gs3d' | 'pointcloud'

interface CameraAnimationAssetKindOption {
  value: CameraAnimationAssetKind
  label: string
  detail: string
}

const ASSET_KIND_OPTIONS: CameraAnimationAssetKindOption[] = [
  { value: 'gs3d', label: '3DGS', detail: '目录内包含 lod-meta.json / meta.json' },
  { value: 'pointcloud', label: '点云', detail: 'Potree V2 目录，入口 metadata.json' },
]

const DEFAULT_POINT_CLOUD_MODEL = '/converted_r/metadata.json'
const SPACEMOUSE_DEFAULT_PROFILE = 'mouse-only' as const
const KEYFRAME_DOWNLOAD_PREFIX = 'share3d-camera-animation'

const loadError = ref('')
const player = ref<AnimationPlayerHandle | null>(null)
const playerState = ref<'idle' | 'playing' | 'paused' | 'completed'>('idle')
const interpolationMode = ref<AnimationInterpolation>('linear')
const easingMode = ref<CameraEasingMode>('sineInOut')
const loopMode = ref<AnimationLoopMode>('once')
const currentCameraFov = ref(65)

const assetKind = ref<CameraAnimationAssetKind>('gs3d')
const pointCloudInput = ref(DEFAULT_POINT_CLOUD_MODEL)
const pointCloudLoading = ref(false)
const pointCloudHandleId = ref<string | null>(null)

// 两类资产共用同一张导入卡片：模式切换只改绑定值与目录入口文件。
const loaderInput = computed({
  get: () => (assetKind.value === 'gs3d' ? modelInput.value : pointCloudInput.value),
  set: (value: string) => {
    if (assetKind.value === 'gs3d') {
      modelInput.value = value
      return
    }
    pointCloudInput.value = value
  },
})

const loaderConfig = computed(() => {
  if (assetKind.value === 'pointcloud') {
    return {
      badge: '点云',
      hint: '请选择包含 metadata.json 的 Potree V2 本地目录，或使用内置示例点云。',
      directoryEntryNames: ['metadata.json'],
      directoryPickLabel: '选择点云目录',
      loadLabel: '加载点云',
    }
  }

  return {
    badge: '3DGS',
    hint: '请选择包含 lod-meta.json 的本地目录，或使用示例默认模型。',
    directoryEntryNames: ['lod-meta.json', 'meta.json'],
    directoryPickLabel: '选择模型目录',
    loadLabel: '加载模型',
  }
})

const loaderLoading = computed(() => isLoading.value || pointCloudLoading.value)

// 点云模式复用 potree demo 的点数/预算卡片与 URL 配置默认值（20M 预算、grouped、10M/draw）。
const pointCloudDemoConfig = readPointCloudDemoUrlConfig()
const pointDrawMode = ref<PointDrawMode>(pointCloudDemoConfig.pointDrawMode)
const groupedDrawPointLimit = ref(pointCloudDemoConfig.groupedDrawPointLimit)
const pointCloudTotalPoints = ref(0)
const pointCloudVisiblePoints = ref(0)
const pointCloudVisibleNodes = ref(0)
const pointBudget = ref(pointCloudDemoConfig.pointBudget)

let pointBudgetRefreshTimer: number | null = null

const statsEnabled = ref(false)
const keyframeIoMessage = ref('')
const keyframeInputRef = useTemplateRef<HTMLInputElement>('keyframeInputRef')

let statsInstance: Stats | null = null
let statsBeforeRenderOff: (() => void) | null = null
let statsAfterRenderOff: (() => void) | null = null

let nextKeyframeId = 1
let nextCameraPlayerId = 1
const cameraKeyframes = ref(DEFAULT_CAMERA_KEYFRAMES.map(createKeyframe))

const activeInterpolationOption = computed(() => {
  return CAMERA_INTERPOLATION_OPTIONS.find((item) => item.value === interpolationMode.value)!
})

const effectiveEasingMode = computed<CameraEasingMode>(() => {
  return interpolationMode.value === 'step' ? 'none' : easingMode.value
})

const activeEasingOption = computed(() => {
  return CAMERA_EASING_OPTIONS.find((item) => item.value === effectiveEasingMode.value)!
})

const activeLoopModeOption = computed(() => {
  return CAMERA_LOOP_MODE_OPTIONS.find((item) => item.value === loopMode.value)!
})

const animationModeMeta = computed(() => {
  return [
    activeInterpolationOption.value.label,
    activeEasingOption.value.label,
    activeLoopModeOption.value.label,
  ].join(' / ')
})

const cameraClipDuration = computed(() => {
  const last = cameraKeyframes.value[cameraKeyframes.value.length - 1]
  return last ? toFiniteNumber(last.time) : 0
})

const modelLoaderHint = computed(() => {
  if (loadError.value) {
    return `加载失败：${loadError.value}`
  }

  return loaderConfig.value.hint
})

const animationStatus = computed(() => {
  switch (playerState.value) {
    case 'playing':
      return '播放中'
    case 'paused':
      return '已暂停'
    case 'completed':
      return '已完成'
    default:
      return '未播放'
  }
})

const controlMode = ref('spacemouse')
const controlModeLabel = computed(() =>
  controlMode.value === 'spacemouse' ? 'SpaceMouse' : controlMode.value
)

const assetSummary = computed(() => {
  const parts: string[] = []
  if (pointCloudHandleId.value) {
    parts.push('点云')
  }
  if (stats.value.totalModels > 0) {
    parts.push(`3DGS ×${stats.value.totalModels}`)
  }
  return parts.length > 0 ? parts.join(' / ') : '未加载'
})

const statusItems = computed(() => [
  {
    label: '播放状态',
    value: animationStatus.value,
  },
  {
    label: '场景资产',
    value: assetSummary.value,
  },
  {
    label: '控制器',
    value: controlModeLabel.value,
  },
  {
    label: '插值方式',
    value: activeInterpolationOption.value.label,
  },
  {
    label: '缓动函数',
    value: activeEasingOption.value.label,
  },
  {
    label: '循环模式',
    value: activeLoopModeOption.value.label,
  },
  {
    label: '关键帧',
    value: `${cameraKeyframes.value.length} 帧 / ${formatNumber(cameraClipDuration.value)}s`,
  },
])

function getAnimationService(): AnimationService | null {
  return engine.value?.context.getService('AnimationService') ?? null
}

function createKeyframe(source: Omit<CameraKeyframe, 'id'>): CameraKeyframe {
  return {
    id: nextKeyframeId++,
    time: source.time,
    position: [...source.position],
    target: [...source.target],
    fov: source.fov,
  }
}

function toFiniteNumber(value: unknown, fallback = 0): number {
  const numeric = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(numeric) ? numeric : fallback
}

function formatNumber(value: unknown, digits = 2): string {
  return Number(toFiniteNumber(value).toFixed(digits)).toString()
}

function roundNumber(value: unknown, digits = 3): number {
  return Number(toFiniteNumber(value).toFixed(digits))
}

function clampNumber(value: unknown, min: number, max: number): number {
  return Math.min(max, Math.max(min, toFiniteNumber(value, min)))
}

function getCurrentCameraState() {
  return engine.value?.camera.getState() ?? null
}

function syncCurrentCameraFov(): void {
  const state = getCurrentCameraState()
  if (state) {
    currentCameraFov.value = roundNumber(state.fov, 1)
  }
}

function applyCameraPatch(patch: CameraStatePatch): void {
  engine.value?.camera.applyStatePatch(patch, {
    cancelTransition: true,
  })
}

function sortKeyframes(): void {
  cameraKeyframes.value.sort((a, b) => a.time - b.time)
}

function getFallbackKeyframeTime(index: number): number {
  if (index <= 0) {
    return 0
  }
  return cameraKeyframes.value[index - 1].time + MIN_KEYFRAME_TIME_GAP
}

function normalizeKeyframeTime(index: number): void {
  const frame = cameraKeyframes.value[index]
  if (!frame) {
    return
  }

  const previous = index > 0 ? cameraKeyframes.value[index - 1] : null
  const next = index < cameraKeyframes.value.length - 1 ?
    cameraKeyframes.value[index + 1] :
    null
  let time = Number(frame.time)

  if (!Number.isFinite(time)) {
    time = getFallbackKeyframeTime(index)
  }

  time = Math.max(0, time)
  if (previous && time <= previous.time) {
    time = previous.time + MIN_KEYFRAME_TIME_GAP
  }
  if (next && time >= next.time) {
    time = getFallbackKeyframeTime(index)
  }
  if (next && time >= next.time) {
    time = Math.max(0, next.time - MIN_KEYFRAME_TIME_GAP)
  }

  frame.time = roundNumber(time)
  handleKeyframeChanged()
}

function normalizeKeyframeVector(
  frame: CameraKeyframe,
  field: CameraVectorField,
  axis: CameraVectorAxis,
): void {
  const value = Number(frame[field][axis])
  frame[field][axis] = Number.isFinite(value) ? roundNumber(value) : 0
  handleKeyframeChanged()
}

function normalizeKeyframeFov(frame: CameraKeyframe): void {
  const value = Number(frame.fov)
  frame.fov = Number.isFinite(value) ?
    roundNumber(clampNumber(value, CAMERA_FOV_MIN, CAMERA_FOV_MAX), 1) :
    65
  handleKeyframeChanged()
}

function handleKeyframeChanged(): void {
  restartActivePlayer()
}

function resolveNewKeyframeTime(): number {
  if (cameraKeyframes.value.length === 0) {
    return 0
  }

  const last = cameraKeyframes.value[cameraKeyframes.value.length - 1]
  let time = player.value?.time ?? ((last?.time ?? 0) + KEYFRAME_TIME_STEP)

  if (!Number.isFinite(time) || time < 0) {
    time = (last?.time ?? 0) + KEYFRAME_TIME_STEP
  }

  const hasSameTime = cameraKeyframes.value.some((frame) => {
    return Math.abs(frame.time - time) < MIN_KEYFRAME_TIME_GAP
  })
  if (hasSameTime) {
    time = (last?.time ?? 0) + KEYFRAME_TIME_STEP
  }

  return roundNumber(time)
}

function addKeyframeFromCamera(): void {
  const state = getCurrentCameraState()
  if (!state) {
    return
  }

  cameraKeyframes.value.push(createKeyframe({
    time: resolveNewKeyframeTime(),
    position: [
      roundNumber(state.position.x),
      roundNumber(state.position.y),
      roundNumber(state.position.z),
    ],
    target: [
      roundNumber(state.target.x),
      roundNumber(state.target.y),
      roundNumber(state.target.z),
    ],
    fov: roundNumber(state.fov, 1),
  }))
  sortKeyframes()
  syncCurrentCameraFov()
  handleKeyframeChanged()
}

function removeKeyframe(index: number): void {
  if (cameraKeyframes.value.length <= 1) {
    return
  }

  cameraKeyframes.value.splice(index, 1)
  handleKeyframeChanged()
}

function clearKeyframes(): void {
  cameraKeyframes.value = []
  stopCameraAnimation()
}

/* ---------- stats.js FPS 面板 ---------- */

/** 挂载 stats.js 面板并绑定引擎 frame.begin/frame.end 事件。 */
function enableStats(): void {
  const engineInstance = engine.value
  if (!engineInstance || statsInstance) {
    return
  }

  const instance = new Stats()
  instance.showPanel(0)
  const dom = instance.dom
  dom.style.position = 'fixed'
  dom.style.left = '16px'
  dom.style.top = '16px'
  dom.style.zIndex = '1100'
  document.body.appendChild(dom)
  statsInstance = instance

  const onBegin = () => {
    instance.begin()
  }
  const onEnd = () => {
    instance.end()
  }

  engineInstance.events.on('frame.begin', onBegin)
  engineInstance.events.on('frame.end', onEnd)
  statsBeforeRenderOff = () => engineInstance.events.off('frame.begin', onBegin)
  statsAfterRenderOff = () => engineInstance.events.off('frame.end', onEnd)
}

/** 解绑事件并移除 stats.js 面板。 */
function disableStats(): void {
  statsBeforeRenderOff?.()
  statsAfterRenderOff?.()
  statsBeforeRenderOff = null
  statsAfterRenderOff = null

  if (statsInstance) {
    statsInstance.dom.remove()
    statsInstance = null
  }
}

watch(statsEnabled, (next) => {
  if (next) {
    enableStats()
    return
  }

  disableStats()
})

/* ---------- 关键帧导入导出 ---------- */

function createKeyframeDownloadName(): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-')
  return `${KEYFRAME_DOWNLOAD_PREFIX}-${stamp}.json`
}

function createCurrentKeyframeFile(): CameraAnimationKeyframeFile {
  return createCameraAnimationKeyframeFile({
    interpolationMode: interpolationMode.value,
    easingMode: effectiveEasingMode.value,
    loopMode: loopMode.value,
    keyframes: cameraKeyframes.value.map((frame) => ({
      time: frame.time,
      position: [...frame.position] as [number, number, number],
      target: [...frame.target] as [number, number, number],
      fov: frame.fov,
    })),
  })
}

function exportKeyframesJson(): void {
  if (cameraKeyframes.value.length === 0) {
    keyframeIoMessage.value = '当前没有关键帧可导出。'
    return
  }

  try {
    const content = stringifyCameraAnimationKeyframeFile(createCurrentKeyframeFile())
    downloadBlob(
      new Blob([content], { type: 'application/json;charset=utf-8' }),
      createKeyframeDownloadName()
    )

    keyframeIoMessage.value = `已导出 ${cameraKeyframes.value.length} 个关键帧。`
  } catch (error) {
    keyframeIoMessage.value = `导出失败：${error instanceof Error ? error.message : String(error)}`
  }
}

function openKeyframeImportPicker(): void {
  const input = keyframeInputRef.value
  if (!input) {
    return
  }

  input.value = ''
  input.click()
}

async function handleKeyframeFileSelected(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) {
    return
  }

  try {
    const parsed = parseCameraAnimationKeyframeFile(await file.text())
    applyImportedKeyframeFile(parsed)
    keyframeIoMessage.value = `已导入 ${file.name}，共 ${parsed.keyframes.length} 个关键帧。`
  } catch (error) {
    keyframeIoMessage.value = `导入失败：${error instanceof Error ? error.message : String(error)}`
  } finally {
    input.value = ''
  }
}

/** 导入后整体替换关键帧与时间轴设置，并把相机位姿对齐到首帧。 */
function applyImportedKeyframeFile(file: CameraAnimationKeyframeFile): void {
  stopCameraAnimation()
  interpolationMode.value = file.interpolationMode
  easingMode.value = file.easingMode
  loopMode.value = file.loopMode

  cameraKeyframes.value = file.keyframes.map((frame) =>
    createKeyframe({
      time: frame.time,
      position: [...frame.position],
      target: [...frame.target],
      fov: frame.fov,
    })
  )

  sortKeyframes()
  syncCurrentCameraFov()
  const firstFrame = cameraKeyframes.value[0]
  if (firstFrame) {
    applyCameraPose(firstFrame)
  }
}

function applyCameraPose(frame: CameraFramePose, stopAnimation = true): void {
  const state = getCurrentCameraState()
  if (!state) {
    return
  }

  if (stopAnimation) {
    stopCameraAnimation()
  }
  state.position.set(frame.position[0], frame.position[1], frame.position[2])
  state.target.set(frame.target[0], frame.target[1], frame.target[2])
  applyCameraPatch({
    position: state.position,
    target: state.target,
    fov: frame.fov,
    roll: 0,
  })
  syncCurrentCameraFov()
}

function applyKeyframeToCamera(frame: CameraKeyframe): void {
  applyCameraPose(frame)
}

function prepareCameraAnimationView(): void {
  useSpaceMouseControl()
  const firstFrame = cameraKeyframes.value[0]
  if (firstFrame) {
    applyCameraPose(firstFrame)
  }
}

function updateCurrentCameraFov(): void {
  const fov = roundNumber(
    clampNumber(currentCameraFov.value, CAMERA_FOV_MIN, CAMERA_FOV_MAX),
    1,
  )
  currentCameraFov.value = fov
  stopCameraAnimation()
  applyCameraPatch({ fov })
}

function getTrackTiming(): Partial<
  Pick<AnimationTrackData, 'interpolation' | 'easing' | 'smoothness'>
> {
  const timing: Partial<
    Pick<AnimationTrackData, 'interpolation' | 'easing' | 'smoothness'>
  > = {
    interpolation: interpolationMode.value,
  }

  if (interpolationMode.value === 'spline') {
    timing.smoothness = 1
  }

  if (effectiveEasingMode.value !== 'none') {
    timing.easing = effectiveEasingMode.value
  }

  return timing
}

function createCameraClip(): AnimationClipData {
  const target = { kind: 'camera' as const, active: 'bind-on-play' as const }
  const frames = cameraKeyframes.value
  const times = frames.map((frame) => frame.time)
  const trackTiming = getTrackTiming()

  return {
    name: '3DGS camera orbit',
    duration: cameraClipDuration.value,
    loopMode: loopMode.value,
    metadata: {
      interpolationMode: interpolationMode.value,
      easingMode: effectiveEasingMode.value,
      loopMode: loopMode.value,
    },
    tracks: [
      {
        target,
        propertyPath: 'position',
        valueType: 'vec3',
        ...trackTiming,
        times,
        values: frames.flatMap((frame) => frame.position),
      },
      {
        target,
        propertyPath: 'target',
        valueType: 'vec3',
        ...trackTiming,
        times,
        values: frames.flatMap((frame) => frame.target),
      },
      {
        target,
        propertyPath: 'fov',
        valueType: 'number',
        ...trackTiming,
        times,
        values: frames.map((frame) => frame.fov),
      },
      {
        target,
        propertyPath: 'roll',
        valueType: 'number',
        ...trackTiming,
        times,
        values: frames.map(() => 0),
      },
    ],
  }
}

function cleanupPlayer(): void {
  const currentPlayer = player.value
  if (currentPlayer) {
    const stoppedByService = getAnimationService()?.stop(currentPlayer.id) ?? false
    if (!stoppedByService) {
      currentPlayer.stop()
    }
  }
  player.value = null
}

function playCameraAnimation(startTime = 0, autoplay = true): void {
  const service = getAnimationService()
  if (!service || cameraKeyframes.value.length === 0) {
    return
  }

  cleanupPlayer()
  const nextPlayer = service.play(createCameraClip(), {
    id: `camera-demo-${nextCameraPlayerId++}`,
    startTime: clampNumber(startTime, 0, Math.max(cameraClipDuration.value, 0)),
    autoplay,
  })
  nextPlayer.on('complete', () => {
    if (player.value === nextPlayer) {
      playerState.value = 'completed'
      player.value = null
      syncCurrentCameraFov()
    }
  })
  player.value = nextPlayer
  playerState.value = autoplay ? 'playing' : 'paused'
}

function pauseCameraAnimation(): void {
  player.value?.pause()
  if (player.value) {
    playerState.value = 'paused'
  }
}

function resumeCameraAnimation(): void {
  player.value?.resume()
  if (player.value) {
    playerState.value = 'playing'
  }
}

function stopCameraAnimation(): void {
  cleanupPlayer()
  playerState.value = 'idle'
}

function restartActivePlayer(): void {
  const currentPlayer = player.value
  if (!currentPlayer || cameraKeyframes.value.length === 0) {
    return
  }

  const shouldAutoplay = playerState.value === 'playing'
  if (!shouldAutoplay && playerState.value !== 'paused') {
    return
  }

  playCameraAnimation(currentPlayer.time, shouldAutoplay)
}

function selectInterpolationMode(mode: AnimationInterpolation): void {
  interpolationMode.value = mode
  restartActivePlayer()
}

function selectEasingMode(mode: CameraEasingMode): void {
  if (interpolationMode.value === 'step' && mode !== 'none') {
    return
  }

  easingMode.value = mode
  restartActivePlayer()
}

function selectLoopMode(mode: AnimationLoopMode): void {
  loopMode.value = mode
  restartActivePlayer()
}

/**
 * 本示例默认使用 SpaceMouse 控制器：兼容普通鼠标与 3D 鼠标，
 * 并复用 gs3d demo 的 `mouse-only` 默认档（航向/俯仰 + 优先捕捉点）。
 */
function getSpaceMouseController(): SpaceMouseControllerLike | null {
  const controller = engine.value?.controls.getController('spacemouse') as
    | SpaceMouseControllerLike
    | undefined

  return controller ?? null
}

function useSpaceMouseControl(): void {
  const engineInstance = engine.value
  if (!engineInstance) {
    return
  }

  engineInstance.controls.setMode('spacemouse')
  applySpaceMouseNavigationProfile(getSpaceMouseController(), SPACEMOUSE_DEFAULT_PROFILE)
  controlMode.value = engineInstance.controls.activeMode
}

async function handleLoadModel(): Promise<void> {
  loadError.value = ''
  stopCameraAnimation()

  try {
    if (stats.value.totalModels > 0) {
      clearSceneModels()
    }
    await loadModel()
    prepareCameraAnimationView()
  } catch (error) {
    loadError.value = error instanceof Error ? error.message : '未知错误'
  }
}

function clearSceneModels(): void {
  stopCameraAnimation()
  const plugin = engine.value?.plugins.get<GaussianSplatPlugin>('gaussian-splat')

  for (const handle of plugin?.getAll() ?? []) {
    plugin?.unmount(handle.id)
    plugin?.dispose(handle.id)
  }

  removeAllModels(false)
  loadError.value = ''
}

function getPointCloudPlugin(): PointCloudPlugin | null {
  return engine.value?.plugins.get<PointCloudPlugin>('pointCloud') ?? null
}

const actualBackend = computed(
  () => graphicsCard.actualBackend.value ?? backendControlState.backend
)
const showPointDrawMode = computed(() => actualBackend.value === 'webgpu')
const showGroupedDrawPointLimit = computed(
  () => showPointDrawMode.value && pointDrawMode.value === 'grouped'
)

/**
 * 点绘制链路与 draw 预算来自 URL 配置；只有 WebGPU 需要显式选择，WebGL2 固定 node 链路。
 * 切换后需要重新加载点云，几何会按新链路重新打包。
 */
function applyPointCloudDemoConfig(): void {
  const plugin = getPointCloudPlugin()
  if (!plugin) {
    return
  }

  const mode = actualBackend.value === 'webgpu' ? pointCloudDemoConfig.pointDrawMode : 'node'
  pointDrawMode.value = mode
  plugin.setConfig({
    renderStrategy: mode === 'grouped' ? 'batched-sprite' : 'default',
    batchPointBudget: groupedDrawPointLimit.value,
  })
}

function syncPointBudgetState(): void {
  const engineInstance = engine.value
  if (!engineInstance) {
    pointCloudTotalPoints.value = 0
    pointCloudVisiblePoints.value = 0
    pointCloudVisibleNodes.value = 0
    pointBudget.value = pointCloudDemoConfig.pointBudget
    return
  }

  let total = 0
  let visible = 0
  let nodes = 0
  for (const handle of getPointCloudPlugin()?.getAll() ?? []) {
    total += handle.totalPointCount ?? 0
    visible += handle.visiblePointCount ?? 0
    nodes += handle.visibleNodeCount ?? 0
  }

  pointCloudTotalPoints.value = total
  pointCloudVisiblePoints.value = visible
  pointCloudVisibleNodes.value = nodes
  pointBudget.value = engineInstance.getPointBudget()
}

function startPointBudgetRefresh(): void {
  stopPointBudgetRefresh()
  pointBudgetRefreshTimer = window.setInterval(syncPointBudgetState, 500)
}

function stopPointBudgetRefresh(): void {
  if (pointBudgetRefreshTimer !== null) {
    window.clearInterval(pointBudgetRefreshTimer)
    pointBudgetRefreshTimer = null
  }
}

/** 预算 / 绘制链路改动需要按新参数重建点云资源，沿用 potree demo 的整页刷新。 */
function reloadPointCloudDemo(patch: PointCloudDemoUrlPatch): void {
  reloadPointCloudDemoConfig(patch)
}

function clearPointCloudScene(): void {
  const plugin = getPointCloudPlugin()

  for (const handle of plugin?.getAll() ?? []) {
    plugin?.unmount(handle.id)
    plugin?.dispose(handle.id)
  }

  pointCloudHandleId.value = null
  loadError.value = ''
}

/**
 * 点云与 3DGS 共用导入卡片：提交时按当前资产类型分派。
 *
 * 点云加载后直接 fit 到资产，让用户能立刻看到导入结果；3DGS 仍沿用首帧镜头位姿。
 */
async function handleLoadPointCloud(): Promise<void> {
  const engineInstance = engine.value
  if (!engineInstance || pointCloudLoading.value) {
    return
  }

  const rawPath = pointCloudInput.value.trim()
  if (!rawPath) {
    loadError.value = '请先选择包含 metadata.json 的本地目录。'
    return
  }

  stopCameraAnimation()
  pointCloudInput.value = rawPath
  loadError.value = ''
  pointCloudLoading.value = true

  try {
    clearPointCloudScene()
    const handle = await engineInstance.loadPointCloud({
      url: resolvePointCloudInputPath(rawPath),
      name: 'Point Cloud',
    })
    pointCloudHandleId.value = handle.id
    engineInstance.fitToAssets([handle.id])
  } catch (error) {
    loadError.value = error instanceof Error ? error.message : '未知错误'
  } finally {
    pointCloudLoading.value = false
  }
}

async function handleLoadAsset(): Promise<void> {
  if (assetKind.value === 'pointcloud') {
    await handleLoadPointCloud()
    return
  }

  await handleLoadModel()
}

function clearSceneAssets(): void {
  clearSceneModels()
  clearPointCloudScene()
}

onBeforeUnmount(() => {
  disableStats()
  stopPointBudgetRefresh()
})

onMounted(async () => {
  graphicsCard.markCreating()
  try {
    await init()
  } catch (error) {
    graphicsCard.markError(toGraphicsRuntimeError(error))
    return
  }
  graphicsCard.markReady(engine.value ? engine.value.getGraphicsInfo().kind : null)
  engine.value?.setPointBudget(pointCloudDemoConfig.pointBudget)
  applyPointCloudDemoConfig()
  syncPointBudgetState()
  startPointBudgetRefresh()
  if (!modelInput.value) {
    modelInput.value = DEFAULT_GS3D_MODEL
  }
  await autoLoad()
  if (stats.value.totalModels === 0) {
    await handleLoadModel()
  }
  prepareCameraAnimationView()
})
</script>

<template>
  <div class="camera-animation-page">
    <div ref="containerRef" class="render-container"></div>

    <button
      class="stats-toggle"
      :class="{ active: statsEnabled }"
      type="button"
      @click="statsEnabled = !statsEnabled"
    >
      {{ statsEnabled ? '隐藏 FPS' : '显示 FPS' }}
    </button>

    <SceneControlPanel
      layout="sidebar"
      kicker="示例面板"
      title="镜头动画"
      :meta="animationModeMeta"
    >
      <SceneAssetLoaderCard
        v-model="loaderInput"
        :title="assetKind === 'pointcloud' ? '加载点云' : '加载模型'"
        :subtitle="
          assetKind === 'pointcloud'
            ? '通过目录选择加载 Potree V2 点云'
            : '通过目录选择加载场景模型'
        "
        :badge="loaderConfig.badge"
        :hint="modelLoaderHint"
        :loading="loaderLoading"
        :can-clear="stats.totalModels > 0 || pointCloudHandleId !== null"
        :directory-entry-names="loaderConfig.directoryEntryNames"
        :directory-pick-label="loaderConfig.directoryPickLabel"
        :load-label="loaderConfig.loadLabel"
        :preset-models="assetKind === 'gs3d' ? PRESET_MODELS : []"
        @submit-load="handleLoadAsset"
        @clear-scene="clearSceneAssets"
      >
        <template #options>
          <div class="asset-kind-switch" role="radiogroup" aria-label="选择要导入的资产类型">
            <button
              v-for="option in ASSET_KIND_OPTIONS"
              :key="option.value"
              class="asset-kind-button"
              :class="{ active: assetKind === option.value }"
              type="button"
              role="radio"
              :aria-checked="assetKind === option.value"
              @click="assetKind = option.value"
            >
              <span class="asset-kind-label">{{ option.label }}</span>
              <span class="asset-kind-detail">{{ option.detail }}</span>
            </button>
          </div>
        </template>
      </SceneAssetLoaderCard>

      <ScenePointBudgetCard
        v-if="assetKind === 'pointcloud'"
        :total-points="pointCloudTotalPoints"
        :visible-points="pointCloudVisiblePoints"
        :visible-node-count="pointCloudVisibleNodes"
        :budget="pointBudget"
        :draw-mode="pointDrawMode"
        :show-draw-mode="showPointDrawMode"
        :grouped-draw-point-limit="groupedDrawPointLimit"
        :show-grouped-draw-point-limit="showGroupedDrawPointLimit"
        @reload-config="reloadPointCloudDemo"
      />

      <GraphicsBackendControlCard :card="graphicsCard">
        <template #extensions>
          <GSplatBackendControlCard :state="backendControlState" />
        </template>
      </GraphicsBackendControlCard>

      <SceneGroupCard
        title="镜头轨道"
        :subtitle="`${activeInterpolationOption.detail} + ${activeEasingOption.detail} + ${activeLoopModeOption.detail}`"
        :badge="animationStatus"
      >
        <div class="status-grid">
          <div v-for="item in statusItems" :key="item.label" class="status-item">
            <span class="status-label">{{ item.label }}</span>
            <strong class="status-value">{{ item.value }}</strong>
          </div>
        </div>

        <div class="button-grid button-grid-two">
          <button
            class="panel-button"
            type="button"
            :disabled="cameraKeyframes.length === 0"
            @click="playCameraAnimation()"
          >
            <span class="panel-button-label">播放</span>
            <span class="panel-button-meta">从起点播放轨道</span>
          </button>
          <button
            class="panel-button panel-button-subtle"
            type="button"
            :disabled="playerState !== 'playing'"
            @click="pauseCameraAnimation"
          >
            <span class="panel-button-label">暂停</span>
            <span class="panel-button-meta">保留当前帧</span>
          </button>
          <button
            class="panel-button panel-button-subtle"
            type="button"
            :disabled="playerState !== 'paused'"
            @click="resumeCameraAnimation"
          >
            <span class="panel-button-label">继续</span>
            <span class="panel-button-meta">接着播放</span>
          </button>
          <button
            class="panel-button panel-button-subtle"
            type="button"
            :disabled="playerState === 'idle'"
            @click="stopCameraAnimation"
          >
            <span class="panel-button-label">停止</span>
            <span class="panel-button-meta">交还相机</span>
          </button>
        </div>

        <div class="choice-section">
          <div class="choice-section-title">插值方式（定义轨迹怎么走）</div>
          <div class="button-grid button-grid-two">
            <button
              v-for="item in CAMERA_INTERPOLATION_OPTIONS"
              :key="item.value"
              class="panel-button"
              :class="{ active: interpolationMode === item.value }"
              type="button"
              @click="selectInterpolationMode(item.value)"
            >
              <span class="panel-button-label">{{ item.label }}</span>
              <span class="panel-button-meta">{{ item.detail }}</span>
            </button>
          </div>
        </div>

        <div class="choice-section">
          <div class="choice-section-title">缓动函数（定义时间怎么走）</div>
          <div class="easing-group-list">
            <div
              v-for="group in CAMERA_EASING_GROUPS"
              :key="group.title"
              class="easing-group"
            >
              <div class="easing-group-title">{{ group.title }}</div>
              <div class="button-grid button-grid-three easing-grid">
                <button
                  v-for="item in group.options"
                  :key="item.value"
                  class="panel-button easing-button"
                  :class="{ active: effectiveEasingMode === item.value }"
                  :disabled="interpolationMode === 'step' && item.value !== 'none'"
                  type="button"
                  @click="selectEasingMode(item.value)"
                >
                  <span class="panel-button-label">{{ item.label }}</span>
                  <span class="panel-button-meta">{{ item.detail }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="choice-section">
          <div class="choice-section-title">循环模式</div>
          <div class="button-grid button-grid-three">
            <button
              v-for="item in CAMERA_LOOP_MODE_OPTIONS"
              :key="item.value"
              class="panel-button"
              :class="{ active: loopMode === item.value }"
              type="button"
              @click="selectLoopMode(item.value)"
            >
              <span class="panel-button-label">{{ item.label }}</span>
              <span class="panel-button-meta">{{ item.detail }}</span>
            </button>
          </div>
        </div>
      </SceneGroupCard>

      <SceneGroupCard
        title="关键帧编辑"
        subtitle="从当前相机捕获关键帧，或直接编辑时间、位置、目标和 FOV"
        :badge="`${cameraKeyframes.length} 帧`"
      >
        <div class="keyframe-toolbar">
          <div class="keyframe-toolbar-main">
            <span class="choice-section-title">Clip 关键帧</span>
            <span class="panel-note">时间必须保持递增</span>
          </div>
          <div class="keyframe-toolbar-actions">
            <button
              class="panel-button panel-button-subtle clear-keyframes-button"
              type="button"
              :disabled="cameraKeyframes.length === 0"
              @click="clearKeyframes"
            >
              <span class="panel-button-label">清空</span>
            </button>
            <button
              class="panel-button icon-action-button"
              type="button"
              :disabled="!engine"
              @click="addKeyframeFromCamera"
            >
              <span class="panel-button-label">+</span>
            </button>
          </div>
        </div>

        <div class="keyframe-io-row">
          <button
            class="panel-button panel-button-subtle"
            type="button"
            :disabled="cameraKeyframes.length === 0"
            @click="exportKeyframesJson"
          >
            <span class="panel-button-label">导出关键帧</span>
            <span class="panel-button-meta">JSON</span>
          </button>
          <button
            class="panel-button panel-button-subtle"
            type="button"
            @click="openKeyframeImportPicker"
          >
            <span class="panel-button-label">导入关键帧</span>
            <span class="panel-button-meta">JSON</span>
          </button>
        </div>
        <input
          ref="keyframeInputRef"
          class="keyframe-file-input"
          type="file"
          accept="application/json,.json"
          @change="handleKeyframeFileSelected"
        />
        <p v-if="keyframeIoMessage" class="panel-note keyframe-io-message">
          {{ keyframeIoMessage }}
        </p>

        <div class="camera-fov-editor">
          <div class="camera-fov-header">
            <span class="choice-section-title">当前镜头 FOV</span>
            <strong>{{ formatNumber(currentCameraFov, 1) }}°</strong>
          </div>
          <div class="camera-fov-controls">
            <input
              v-model.number="currentCameraFov"
              class="panel-range"
              type="range"
              :min="CAMERA_FOV_MIN"
              :max="CAMERA_FOV_MAX"
              step="0.1"
              @input="updateCurrentCameraFov"
            />
            <input
              v-model.number="currentCameraFov"
              class="keyframe-input fov-number-input"
              type="number"
              :min="CAMERA_FOV_MIN"
              :max="CAMERA_FOV_MAX"
              step="0.1"
              @change="updateCurrentCameraFov"
            />
          </div>
        </div>

        <div class="keyframe-list">
          <p v-if="cameraKeyframes.length === 0" class="panel-note empty-keyframe-note">
            暂无关键帧，可点击右上角加号从当前镜头捕获一帧。
          </p>
          <div
            v-for="(frame, index) in cameraKeyframes"
            :key="frame.id"
            class="keyframe-row"
          >
            <div class="keyframe-row-header">
              <strong>关键帧 {{ index + 1 }}</strong>
              <label class="keyframe-time-field">
                <span>时间</span>
                <input
                  v-model.number="frame.time"
                  class="keyframe-input"
                  type="number"
                  min="0"
                  step="0.01"
                  @change="normalizeKeyframeTime(index)"
                />
              </label>
              <div class="keyframe-actions">
                <button
                  class="small-icon-button"
                  type="button"
                  title="切换到该关键帧"
                  @click="applyKeyframeToCamera(frame)"
                >
                  定位
                </button>
                <button
                  class="small-icon-button danger"
                  type="button"
                  title="删除关键帧"
                  :disabled="cameraKeyframes.length <= 1"
                  @click="removeKeyframe(index)"
                >
                  -
                </button>
              </div>
            </div>

            <div class="keyframe-vector-grid">
              <div class="keyframe-vector-editor">
                <span class="keyframe-vector-title">位置</span>
                <label
                  v-for="axis in CAMERA_VECTOR_AXES"
                  :key="`position-${axis.label}`"
                  class="keyframe-axis-field"
                >
                  <span>{{ axis.label }}</span>
                  <input
                    v-model.number="frame.position[axis.axis]"
                    class="keyframe-input"
                    type="number"
                    step="0.001"
                    @change="normalizeKeyframeVector(frame, 'position', axis.axis)"
                  />
                </label>
              </div>

              <div class="keyframe-vector-editor">
                <span class="keyframe-vector-title">目标</span>
                <label
                  v-for="axis in CAMERA_VECTOR_AXES"
                  :key="`target-${axis.label}`"
                  class="keyframe-axis-field"
                >
                  <span>{{ axis.label }}</span>
                  <input
                    v-model.number="frame.target[axis.axis]"
                    class="keyframe-input"
                    type="number"
                    step="0.001"
                    @change="normalizeKeyframeVector(frame, 'target', axis.axis)"
                  />
                </label>
              </div>

              <label class="keyframe-fov-field">
                <span class="keyframe-vector-title">FOV</span>
                <input
                  v-model.number="frame.fov"
                  class="keyframe-input"
                  type="number"
                  :min="CAMERA_FOV_MIN"
                  :max="CAMERA_FOV_MAX"
                  step="0.1"
                  @change="normalizeKeyframeFov(frame)"
                />
              </label>
            </div>
          </div>
        </div>
      </SceneGroupCard>
    </SceneControlPanel>
  </div>
</template>

<style scoped>
.camera-animation-page {
  --panel-gap: 0px;
  width: 100%;
  height: 100vh;
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(0, 3fr);
  gap: var(--panel-gap);
  padding: var(--panel-gap);
  box-sizing: border-box;
  align-items: stretch;
  background: #02050a;
  color: #eef4ff;
}

.render-container {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  position: relative;
}

.choice-section {
  display: grid;
  gap: 8px;
}

.choice-section-title {
  color: #9fb0c6;
  font-size: 12px;
  font-weight: 700;
}

.easing-group-list {
  display: grid;
  gap: 10px;
}

.easing-group {
  display: grid;
  gap: 7px;
}

.easing-group-title {
  color: #c3d6e4;
  font-size: 12px;
  font-weight: 700;
}

:deep(.easing-grid) {
  gap: 8px;
}

:deep(.easing-button) {
  min-height: 56px;
  padding: 8px 9px;
}

:deep(.easing-button .panel-button-label) {
  font-size: 13px;
}

:deep(.easing-button .panel-button-meta) {
  font-size: 11px;
}

.keyframe-toolbar,
.camera-fov-header,
.camera-fov-controls,
.keyframe-row-header,
.keyframe-actions,
.keyframe-toolbar-actions {
  display: flex;
  align-items: center;
}

.keyframe-toolbar,
.keyframe-row-header {
  justify-content: space-between;
  gap: 10px;
}

.keyframe-toolbar-main {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.keyframe-toolbar-actions {
  gap: 8px;
  flex: 0 0 auto;
}

.clear-keyframes-button {
  min-height: 38px;
  padding: 8px 12px;
}

.keyframe-io-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.keyframe-file-input {
  display: none;
}

.keyframe-io-message {
  margin: 0;
}

.asset-kind-switch {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.asset-kind-button {
  display: grid;
  gap: 2px;
  padding: 8px 10px;
  border: 1px solid rgba(152, 191, 214, 0.18);
  border-radius: 10px;
  background: rgba(6, 10, 16, 0.52);
  color: #dce9f5;
  text-align: left;
  cursor: pointer;
}

.asset-kind-button.active {
  border-color: rgba(67, 220, 196, 0.55);
  background: rgba(67, 220, 196, 0.14);
}

.asset-kind-label {
  font-size: 13px;
  font-weight: 700;
}

.asset-kind-detail {
  color: #90aabe;
  font-size: 11px;
}

.stats-toggle {
  position: absolute;
  left: 24px;
  bottom: 24px;
  z-index: 1100;
  padding: 8px 14px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  backdrop-filter: blur(4px);
}

.stats-toggle.active {
  background: rgba(64, 160, 255, 0.85);
  border-color: rgba(255, 255, 255, 0.6);
}

:deep(.icon-action-button) {
  width: 42px;
  min-height: 38px;
  padding: 8px;
  text-align: center;
}

:deep(.icon-action-button .panel-button-label) {
  font-size: 20px;
  line-height: 1;
}

.camera-fov-editor {
  display: grid;
  gap: 10px;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.03);
}

.camera-fov-header {
  justify-content: space-between;
  gap: 10px;
}

.camera-fov-header strong {
  color: #f7fbff;
  font-size: 14px;
}

.camera-fov-controls {
  gap: 10px;
}

.panel-range {
  width: 100%;
  min-width: 0;
  accent-color: #43dcc4;
}

.fov-number-input {
  width: 72px;
  flex: 0 0 auto;
}

.keyframe-list {
  display: grid;
  gap: 10px;
}

.empty-keyframe-note {
  margin: 0;
  padding: 12px;
  border-radius: 12px;
  border: 1px dashed rgba(152, 191, 214, 0.18);
  background: rgba(255, 255, 255, 0.03);
}

.keyframe-row {
  display: grid;
  gap: 10px;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  background: rgba(2, 8, 14, 0.36);
}

.keyframe-row-header strong {
  min-width: 72px;
  color: #f7fbff;
  font-size: 13px;
}

.keyframe-time-field,
.keyframe-axis-field,
.keyframe-fov-field {
  display: grid;
  gap: 4px;
}

.keyframe-time-field {
  width: 92px;
}

.keyframe-time-field span,
.keyframe-axis-field span {
  color: #90aabe;
  font-size: 11px;
  font-weight: 700;
}

.keyframe-actions {
  gap: 6px;
}

.small-icon-button {
  min-height: 32px;
  padding: 6px 9px;
  border: 1px solid rgba(141, 179, 201, 0.2);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  color: #eef6fb;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.small-icon-button:hover:not(:disabled) {
  border-color: rgba(160, 209, 238, 0.42);
  background: rgba(124, 188, 220, 0.12);
}

.small-icon-button.danger {
  min-width: 32px;
  color: #ffd5d5;
}

.small-icon-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.keyframe-vector-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 10px;
}

.keyframe-vector-editor {
  display: grid;
  grid-template-columns: 42px repeat(3, minmax(0, 1fr));
  gap: 7px;
  align-items: end;
}

.keyframe-vector-title {
  align-self: center;
  color: #c3d6e4;
  font-size: 12px;
  font-weight: 700;
}

.keyframe-fov-field {
  grid-template-columns: 42px minmax(0, 1fr);
  align-items: center;
}

.keyframe-input {
  width: 100%;
  min-width: 0;
  min-height: 34px;
  padding: 7px 8px;
  border: 1px solid rgba(152, 191, 214, 0.18);
  border-radius: 10px;
  background: rgba(6, 10, 16, 0.52);
  color: #f3f8fc;
  font-size: 12px;
  outline: none;
}

.keyframe-input:focus {
  border-color: rgba(121, 201, 197, 0.55);
  box-shadow: 0 0 0 2px rgba(121, 201, 197, 0.14);
}

@media (max-width: 900px) {
  /* 面板在移动端已改为悬浮展示（见 SceneControlPanel），画布铺满整页即可 */
  .camera-animation-page {
    grid-template-columns: 1fr;
  }
}
</style>
